import { HttpParams, HttpRequest } from '@angular/common/http';
import { Injectable, Injector, Provider, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { OutboxEntry } from 'src/app/core/offline/offline.models';
import {
  ConflictField,
  HandlerContext,
  OUTBOX_HANDLERS,
  OutboxHandler,
} from 'src/app/core/offline/outbox-handler';
import { environment } from 'src/environments/environment';
import {
  DocumentUploadFields,
  DocumentUploadPayload,
  PinOpPayload,
} from './document-offline.models';
import { DocumentOfflineService } from './document-offline.service';

// Offline E2 step 4: the document handlers of the offline core (spec §4.1). Provided in the AppModule, so the sync finds
// them on every page. The online writes of DocumentOfflineService and DocumentPinOfflineService build their requests
// here too: an interrupted online write keeps URL and body in the outbox (plan D7 of step 3).

export function documentsUrl(projectId: string): string {
  return `${environment.apiUrl}/projects/${encodeURIComponent(projectId)}/documents`;
}

export function pinsUrl(projectId: string, documentKey: string): string {
  return `${documentsUrl(projectId)}/${encodeURIComponent(documentKey)}/pins`;
}

/** blank text is left out: the backend refuses an empty description or category (403, stored per key; Rev1 BE-W1) */
export function textOrNull(value: string | null | undefined): string | null {
  return value && value.trim() ? value : null;
}

/** 404 on removing a pin that means «already gone»; «Not Found» of the access check (no member any more) is no success */
export const PIN_GONE_ERRORS: readonly string[] = [
  'Pin not found',
  'Document not found',
  'Task not found',
];

/**
 * The multipart body of POST /projects/:id/documents, field by the rules of the former online form (pre-step-4 new-datenraum
 * convertToFormData, removed): arrays as name[], null and blank text left out, the file under «files». The backend hashes fields,
 * file name, type and content (idempotency), so the same payload gives the same hash on every try.
 */
export function documentUploadForm(
  fields: DocumentUploadFields,
  file: File,
): FormData {
  const form = new FormData();
  form.append('name', fields.name);
  const category = textOrNull(fields.category);
  const description = textOrNull(fields.description);
  if (category !== null) form.append('category', category);
  if (description !== null) form.append('description', description);
  for (const tag of fields.tags) form.append('tags[]', tag);
  form.append('external', String(fields.external));
  form.append('files', file, file.name);
  for (const bkp of fields.bkps) form.append('bkps[]', bkp);
  if (fields.predecessorKey)
    form.append('predecessorKey', fields.predecessorKey);
  return form;
}

@Injectable()
export class DocumentUploadHandler implements OutboxHandler {
  readonly type = 'document.upload' as const;
  readonly creates = true;
  private readonly translate = inject(TranslateService);
  /** lazy: DocumentOfflineService needs the SyncEngine, which needs the handlers */
  private readonly injector = inject(Injector);

  async build(
    entry: OutboxEntry,
    ctx: HandlerContext,
  ): Promise<HttpRequest<unknown>> {
    const { projectId, fields, fileName, mime, blobIds } =
      entry.payload as unknown as DocumentUploadPayload;
    const blob = blobIds?.[0] ? await ctx.getBlob(blobIds[0]) : undefined;
    if (!blob) throw new Error('document.upload without its file');
    const file =
      blob instanceof File && blob.name === fileName
        ? blob
        : new File([blob], fileName, { type: blob.type || mime });
    return new HttpRequest(
      'POST',
      documentsUrl(projectId),
      documentUploadForm(fields, file),
    );
  }

  /**
   * The routes name documents by key: the id map keeps local id → server key (pins on an offline upload, plan E7).
   * The file stays on the device as saved plan (E14 revised, NA-59) before the engine removes the entry and its blob.
   */
  async onSuccess(entry: OutboxEntry, body: unknown): Promise<string | null> {
    const key = (body as { data?: { key?: unknown }[] } | null)?.data?.[0]?.key;
    if (typeof key !== 'string') return null;
    await this.injector.get(DocumentOfflineService).keepUploadedFile(entry, body);
    return key;
  }

  describe(entry: OutboxEntry): string {
    const { fields } = entry.payload as unknown as DocumentUploadPayload;
    return `${this.translate.instant('offline.types.document.upload')} – ${fields.name}`;
  }

  copyText(entry: OutboxEntry): string {
    const { fields, fileName } =
      entry.payload as unknown as DocumentUploadPayload;
    return [fields.name, fileName, fields.description ?? '']
      .filter(Boolean)
      .join('\n');
  }
}

function pinText(translate: TranslateService, payload: PinOpPayload): string {
  return translate.instant(`offline.pin.positions`, {
    page: payload.pageNumber ?? '–',
    x: Math.round((payload.x ?? 0) * 100),
    y: Math.round((payload.y ?? 0) * 100),
  });
}

@Injectable()
export class DocumentPinHandler implements OutboxHandler {
  readonly type = 'document.pin' as const;
  /**
   * A pin to remove is gone already (removed elsewhere, document or task deleted, our own retry after the commit): done
   * (like task.delete, D6). A 404 of the access check (no member any more) fails visibly (Rev1, BE review W2).
   */
  readonly doneOnStatus = (
    entry: OutboxEntry,
    body: unknown,
  ): readonly number[] => {
    const error = (body as { error?: unknown } | null)?.error;
    return (entry.payload as unknown as PinOpPayload).action === 'delete' &&
      typeof error === 'string' &&
      PIN_GONE_ERRORS.includes(error)
      ? [404]
      : [];
  };
  /**
   * A delete (or a later move) chains to a sent move only for the order (N-H1). «Neuere übernehmen» of that move must not
   * take the removal with it (Rev1 H1): the dependents stay and go out with the server version
   * (ConflictService.takeTheirs rebases them first, R-H4/D49a: no own conflict, the user already saw the server state). A 409 «Document is superseded» on a delete stays failed: the pin may live on the
   * successor plan (BE review H2 considered, not taken).
   */
  keepDependentsOnTheirs(entry: OutboxEntry): boolean {
    const action = (entry.payload as unknown as PinOpPayload).action;
    return action === 'move' || action === 'confirm';
  }
  private readonly translate = inject(TranslateService);

  async build(entry: OutboxEntry): Promise<HttpRequest<unknown>> {
    const payload = entry.payload as unknown as PinOpPayload;
    const url = pinsUrl(payload.projectId, payload.documentKey);
    const version =
      payload.ifVersion === undefined ? {} : { ifVersion: payload.ifVersion };
    switch (payload.action) {
      case 'create':
        return new HttpRequest('POST', url, {
          taskId: payload.taskId,
          pageNumber: payload.pageNumber,
          x: payload.x,
          y: payload.y,
        });
      case 'move':
        return new HttpRequest(
          'PATCH',
          `${url}/${encodeURIComponent(payload.taskId)}`,
          {
            pageNumber: payload.pageNumber,
            x: payload.x,
            y: payload.y,
            ...version,
          },
        );
      case 'confirm':
        return new HttpRequest(
          'PATCH',
          `${url}/${encodeURIComponent(payload.taskId)}`,
          version,
        );
      case 'delete':
        // E-3 (NA-55): optional ifVersion as query; a newer pin on the server → 409 VERSION_CONFLICT (conflict dialog)
        return new HttpRequest(
          'DELETE',
          `${url}/${encodeURIComponent(payload.taskId)}`,
          payload.ifVersion === undefined
            ? {}
            : {
                params: new HttpParams().set(
                  'ifVersion',
                  String(payload.ifVersion),
                ),
              },
        );
    }
  }

  describe(entry: OutboxEntry): string {
    const payload = entry.payload as unknown as PinOpPayload;
    const action = this.translate.instant(
      `offline.pin.actions.${payload.action}`,
    );
    return `${action} – ${payload.label || payload.documentName}`;
  }

  copyText(entry: OutboxEntry): string {
    const payload = entry.payload as unknown as PinOpPayload;
    return [
      this.describe(entry),
      payload.documentName,
      payload.action === 'create' || payload.action === 'move'
        ? pinText(this.translate, payload)
        : '',
    ]
      .filter(Boolean)
      .join('\n');
  }

  /**
   * move: my position against the newer one; confirm: the newer position stays, only «bestätigt» is mine; delete
   * (E-3): «Pin entfernt» against the newer pin
   */
  conflictFields(
    entry: OutboxEntry,
    current: Record<string, unknown>,
  ): ConflictField[] {
    const payload = entry.payload as unknown as PinOpPayload;
    const theirs = {
      ...payload,
      pageNumber: current['pageNumber'] as number | undefined,
      x: current['x'] as number | undefined,
      y: current['y'] as number | undefined,
    };
    const confirmed = this.translate.instant('offline.pin.confirmed');
    const unconfirmed = this.translate.instant('offline.pin.unconfirmed');
    return [
      {
        key: 'position',
        label: this.translate.instant('offline.pin.fields.position'),
        mine:
          payload.action === 'delete'
            ? this.translate.instant('offline.pin.actions.delete')
            : payload.action === 'move'
              ? pinText(this.translate, payload)
              : pinText(this.translate, theirs),
        theirs: pinText(this.translate, theirs),
      },
      {
        key: 'verification',
        label: this.translate.instant('offline.pin.fields.verification'),
        mine: payload.action === 'delete' ? '–' : confirmed,
        theirs:
          current['verification'] === 'unverified' ? unconfirmed : confirmed,
      },
    ];
  }
}

export const DOCUMENT_OUTBOX_HANDLERS: Provider[] = [
  { provide: OUTBOX_HANDLERS, useClass: DocumentUploadHandler, multi: true },
  { provide: OUTBOX_HANDLERS, useClass: DocumentPinHandler, multi: true },
];
